// VRAM breakdown shared by the on-screen chart (VramBreakdownChart) and the PDF
// report, so both show the same slices, colours and explanations.
import type { CalculationResult } from "@/types/calculator";

export type SliceKey = "weights" | "kvCache" | "activations" | "rag" | "overhead" | "free";

export interface VramSlice {
  key: SliceKey;
  label: string;
  gb: number;
  pct: number; // share of installed VRAM
  detail: string; // how the number was derived, in plain words
}

// Lenovo red ramp, following the old pie: darkest = occupied by the model,
// lighter = runtime overhead, palest = free headroom. Dark mode steps the same
// order lighter so the used slices stay prominent on the dark surface.
export const SLICE_COLORS: Record<SliceKey, { light: string; dark: string }> = {
  weights: { light: "#5c0000", dark: "#ffb3b3" },
  kvCache: { light: "#8f0000", dark: "#ff8585" },
  activations: { light: "#b30000", dark: "#f25555" },
  rag: { light: "#d93636", dark: "#d42e2e" },
  overhead: { light: "#f76767", dark: "#a32424" },
  free: { light: "#fca5a5", dark: "#6e2626" },
};

const fmtGb = (gb: number) => `${gb.toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`;
const fmtNum = (n: number, digits = 0) => n.toLocaleString(undefined, { maximumFractionDigits: digits });

export interface VramSummary {
  slices: VramSlice[]; // in stacking order; zero-size RAG is omitted, free only when > 0
  totalGb: number;
  usedGb: number;
  shortfallGb: number; // > 0 when the workload doesn't fit
  scaleGb: number; // what the chart spans: installed VRAM, or the requirement when it's larger
  gpuCount: number;
  vramPerGpu: number;
}

export function buildVramSummary(result: CalculationResult): VramSummary {
  const v = result.vramBreakdown;
  const precision = result.inputSummary.precision;
  const total = v.total;
  const pct = (gb: number) => (total > 0 ? (gb / total) * 100 : 0);
  const bytes = v.bytesPerParam;
  const users = v.users;
  const userWord = users === 1 ? "user" : "users";

  const slices: VramSlice[] = [
    {
      key: "weights",
      label: "Model weights",
      gb: v.weights,
      pct: pct(v.weights),
      detail: `${fmtNum(v.paramsB, 1)}B parameters × ${bytes} byte${bytes === 1 ? "" : "s"} each (${precision})` +
        (v.copies > 1 ? ` × ${v.copies} model copies` : ""),
    },
    {
      key: "kvCache",
      label: "KV cache",
      gb: v.kvCache,
      pct: pct(v.kvCache),
      detail: `${fmtNum(users)} ${userWord} × ${fmtNum(v.kvPerUser, 2)} GB for ${fmtNum(v.sequenceTokens)} tokens of context each`,
    },
    {
      key: "activations",
      label: "Activations",
      gb: v.activations,
      pct: pct(v.activations),
      detail: `Working buffer for a batch of up to ${fmtNum(v.activationBatchTokens)} tokens` +
        (v.copies > 1 ? ` (${fmtNum(v.activationPerCopy, 2)} GB × ${v.copies} model copies)` : ""),
    },
  ];
  if (v.rag > 0) {
    slices.push({
      key: "rag",
      label: "RAG embedding model",
      gb: v.rag,
      pct: pct(v.rag),
      detail: "~0.4B-parameter embedding model held in FP16",
    });
  }
  slices.push({
    key: "overhead",
    label: "Reserved overhead",
    gb: v.overhead,
    pct: pct(v.overhead),
    detail: `${fmtNum(result.inputSummary.gpuOverhead, 1)}% of VRAM held back for the runtime (CUDA context, graphs, fragmentation)`,
  });
  if (v.free > 0) {
    slices.push({
      key: "free",
      label: "Free headroom",
      gb: v.free,
      pct: pct(v.free),
      detail:
        v.extraUsers > 0
          ? `Room for about ${fmtNum(v.extraUsers)} more concurrent ${v.extraUsers === 1 ? "user" : "users"} at these token lengths`
          : "Less than one more user's worth of memory",
    });
  }

  const required = v.used + v.overhead;
  return {
    slices,
    totalGb: total,
    usedGb: v.used,
    shortfallGb: Math.max(0, Math.round((required - total) * 10) / 10),
    scaleGb: Math.max(total, required),
    gpuCount: v.gpuCount,
    vramPerGpu: v.vramPerGpu,
  };
}

export { fmtGb };

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// SVG path for one donut slice between angles a0..a1 (radians, clockwise from
// 12 o'clock). A full-circle slice is split in two so the arc stays drawable.
export function donutArcPath(cx: number, cy: number, rInner: number, rOuter: number, a0: number, a1: number): string {
  if (a1 - a0 >= Math.PI * 2 - 1e-6) {
    const mid = a0 + Math.PI;
    return donutArcPath(cx, cy, rInner, rOuter, a0, mid) + " " + donutArcPath(cx, cy, rInner, rOuter, mid, a1);
  }
  const pt = (r: number, a: number) => `${cx + r * Math.sin(a)} ${cy - r * Math.cos(a)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return [
    `M ${pt(rOuter, a0)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${pt(rOuter, a1)}`,
    `L ${pt(rInner, a1)}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${pt(rInner, a0)}`,
    "Z",
  ].join(" ");
}

// Render the donut to a PNG data URL for the PDF (same slices and light-mode
// colours as the on-screen chart). Returns "" if a 2D canvas is unavailable.
export function drawDonutPng(summary: VramSummary, sizePx = 520): string {
  const canvas = document.createElement("canvas");
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext("2d");
  if (!ctx || summary.scaleGb <= 0) return "";
  const c = sizePx / 2;
  const rOuter = sizePx * 0.46;
  const rInner = rOuter * 0.62;
  let angle = 0;
  for (const slice of summary.slices) {
    const sweep = (slice.gb / summary.scaleGb) * Math.PI * 2;
    if (sweep <= 0) continue;
    ctx.fillStyle = SLICE_COLORS[slice.key].light;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = sizePx * 0.008;
    ctx.fill(new Path2D(donutArcPath(c, c, rInner, rOuter, angle, angle + sweep)));
    ctx.stroke(new Path2D(donutArcPath(c, c, rInner, rOuter, angle, angle + sweep)));
    angle += sweep;
  }
  return canvas.toDataURL("image/png");
}
